import type { SvgIconProps } from "@mui/material";
import type { ComponentType } from "react";

import AccountBalanceOutlined from "@mui/icons-material/AccountBalanceOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import ScienceOutlined from "@mui/icons-material/ScienceOutlined";
import WorkspacePremium from "@mui/icons-material/WorkspacePremium";

export type ScoutSuggestion = {
  /**
   * Optional short text shown in the disclosure row. When omitted, the row
   * shows `prompt`. Used today for My Program upsell chips so the list stays
   * punchy while the sent prompt can be a longer self-upsell walkthrough.
   */
  label?: string;
  /** The full prompt sent to Scout when the row is picked. */
  prompt: string;
  /**
   * True when answering needs customer-scoped Encamp data lookups
   * (facilities, inventories, reports, portfolio roll-ups, etc.).
   */
  requiresDataAccess: boolean;
  /**
   * True when answering needs the Scout code sandbox (typically uploaded-file
   * / document analysis via sandbox tools).
   */
  requiresSandbox: boolean;
};

export type ScoutSuggestionCategory = {
  key: string;
  /** Short label for the category chip (kept brief so chips fit on one line). */
  label: string;
  Icon: ComponentType<SvgIconProps>;
  items: ScoutSuggestion[];
};

/**
 * "My Program" is a Regulatory Advisor–only Scout Pro upsell (WorkspacePremium).
 * Hidden for Pro; included in full for Regulatory Advisor via
 * `getVisibleScoutSuggestionCategories`.
 */
export const MY_PROGRAM_CATEGORY_KEY = "my-program";

/** Text shown in the suggestion list row (`label` when set, otherwise `prompt`). */
export function getScoutSuggestionDisplayText(item: ScoutSuggestion): string {
  return item.label ?? item.prompt;
}

/**
 * Progressive-disclosure starter prompts for the Scout new-chat screen.
 *
 * Each category includes:
 * - Regulatory Advisor–safe prompts (`requiresDataAccess` and `requiresSandbox`
 *   both false) answered from compliance / regulation / Academy / support corpora
 * - Scout Pro prompts that need customer data and/or the code sandbox
 *
 * Pro / portfolio prompts are framed as PROGRAM-level questions (counts,
 * breakdowns, roll-ups). Documents Pro prompts invite an upload after Scout
 * explains the analysis workflow.
 */
export const SCOUT_SUGGESTION_CATEGORIES: ScoutSuggestionCategory[] = [
  {
    key: "agencies",
    label: "Agencies",
    Icon: AccountBalanceOutlined,
    items: [
      {
        prompt:
          "Under EPCRA, who must receive a facility's annual Tier II report — SERC, LEPC, and local fire department — and when do those obligations differ by state?",
        requiresDataAccess: false,
        requiresSandbox: false,
      },
      {
        prompt:
          "How do reporting thresholds and requirements differ between New York State and New York City?",
        requiresDataAccess: false,
        requiresSandbox: false,
      },
      {
        prompt:
          "In California, how do CUPA / CalARP / HMBP obligations relate to federal EPCRA Tier II, and which agency actually receives the local filing?",
        requiresDataAccess: false,
        requiresSandbox: false,
      },
      {
        prompt:
          "Give me a program-level overview of our regulatory footprint: how many facilities we have, across how many states, and which reporting programs (EPCRA, RCRA, and others) they fall under.",
        requiresDataAccess: true,
        requiresSandbox: false,
      },
      {
        prompt:
          "Summarize how our reporting obligations and deadlines differ across the states where we operate.",
        requiresDataAccess: true,
        requiresSandbox: false,
      },
      {
        prompt:
          "Which states and jurisdictions place the most reporting requirements on us, and how many of our facilities are affected?",
        requiresDataAccess: true,
        requiresSandbox: false,
      },
    ],
  },
  {
    key: "chemicals",
    label: "Chemicals",
    Icon: ScienceOutlined,
    items: [
      {
        prompt:
          "Explain the federal EPCRA Tier II reporting thresholds for hazardous chemicals and Extremely Hazardous Substances (TPQ / threshold planning quantity), including how mixtures are typically handled.",
        requiresDataAccess: false,
        requiresSandbox: false,
      },
      {
        prompt:
          "What is due by March 1 for EPCRA Tier II, what does a complete filing generally include, and which agencies typically receive it?",
        requiresDataAccess: false,
        requiresSandbox: false,
      },
      {
        prompt:
          "What additional Tier II data collection requirements does Kansas impose beyond the federal EPCRA baseline?",
        requiresDataAccess: false,
        requiresSandbox: false,
      },
      {
        prompt:
          "Give me a program-wide overview of our Tier II chemical reporting. If we're currently in Tier II reporting season (before March 1), summarize our filing status across the portfolio — how many facilities are filed, in progress, and not started. If the season is over (after March 1), summarize the last season instead: reports filed by state and filing fees by state.",
        requiresDataAccess: true,
        requiresSandbox: false,
      },
      {
        prompt:
          "How many of our facilities have chemicals over Tier II reporting thresholds, and which states are they concentrated in?",
        requiresDataAccess: true,
        requiresSandbox: false,
      },
      {
        prompt:
          "Which states require additional Tier II data from us, and how many of our facilities does that affect?",
        requiresDataAccess: true,
        requiresSandbox: false,
      },
    ],
  },
  {
    key: "hazardous-waste",
    label: "Waste",
    Icon: DeleteOutline,
    items: [
      {
        prompt:
          "Explain how federal RCRA distinguishes VSQG, SQG, and LQG generator categories — including the monthly generation thresholds and the main obligation differences.",
        requiresDataAccess: false,
        requiresSandbox: false,
      },
      {
        prompt:
          "What is the RCRA Biennial Hazardous Waste Report, who must file it, what period does it cover, and when is it typically due?",
        requiresDataAccess: false,
        requiresSandbox: false,
      },
      {
        prompt:
          "Can you tell me about the RCRA waste reporting schedule and key generator deadlines in Arizona?",
        requiresDataAccess: false,
        requiresSandbox: false,
      },
      {
        prompt:
          "Give me a portfolio-wide breakdown of our hazardous waste generator status (VSQG, SQG, LQG) across all facilities.",
        requiresDataAccess: true,
        requiresSandbox: false,
      },
      {
        prompt:
          "Across all of our facilities, what are our largest hazardous waste streams by volume?",
        requiresDataAccess: true,
        requiresSandbox: false,
      },
      {
        prompt:
          "What annual and biennial hazardous waste reports are coming due across our facilities, and when?",
        requiresDataAccess: true,
        requiresSandbox: false,
      },
    ],
  },
  {
    key: "documents",
    label: "Documents",
    Icon: DescriptionOutlined,
    items: [
      {
        prompt:
          "What hazardous waste records must a large quantity generator (LQG) keep on site, and for how long under federal RCRA?",
        requiresDataAccess: false,
        requiresSandbox: false,
      },
      {
        prompt:
          "Under OSHA HazCom, how long must employers retain Safety Data Sheets, and what are the common pitfalls when chemicals are discontinued?",
        requiresDataAccess: false,
        requiresSandbox: false,
      },
      {
        prompt:
          "What documents and attachments typically make up a complete EPCRA Tier II submission package, beyond the chemical inventory itself?",
        requiresDataAccess: false,
        requiresSandbox: false,
      },
      {
        prompt:
          "I want to analyze a permit. Before I upload anything, walk me through what you can pull out of it and how to get started.",
        requiresDataAccess: false,
        requiresSandbox: true,
      },
      {
        prompt:
          "I'd like to extract the key dates, limits, and obligations from a compliance document. Explain how that works here and what you need from me, then I'll upload it.",
        requiresDataAccess: false,
        requiresSandbox: true,
      },
      {
        prompt:
          "I have a long permit or report I want summarized. Describe how document analysis works before I drag it in.",
        requiresDataAccess: false,
        requiresSandbox: true,
      },
      {
        prompt:
          "I want to compare two versions of a permit. Tell me how you'd approach it and what to provide, then I'll add the files.",
        requiresDataAccess: false,
        requiresSandbox: true,
      },
      {
        prompt:
          "Help me set up a review of a regulatory document — explain the steps first, then I'll upload it.",
        requiresDataAccess: false,
        requiresSandbox: true,
      },
    ],
  },
  {
    key: "my-program",
    label: "My Program",
    Icon: WorkspacePremium,
    items: [
      {
        label:
          "How can you help me understand a permit document and build a calendar with tasks and deadlines?",
        prompt:
          "I'm exploring Scout Pro. Can you explain how Scout Pro would help me understand a permit document and turn it into a calendar of tasks and deadlines — what you'd extract from the permit, how you'd structure the calendar, what I'd need to upload, and what value Pro would provide compared to Regulatory Advisor alone?",
        requiresDataAccess: false,
        requiresSandbox: true,
      },
      {
        label: "Could you review my portfolio and generate reports based on it?",
        prompt:
          "I'm exploring Scout Pro. Can you explain how Scout Pro would review my facility portfolio to generate useful reports from it, and what value Pro would provide? What kinds of outputs could you produce compared to Regulatory Advisor alone, and what kinds of data can you see that Regulatory Advisor cannot?",
        requiresDataAccess: true,
        requiresSandbox: false,
      },
      {
        label:
          "Can you help me understand which reports should be updated due to a team member leaving my company?",
        prompt:
          "I'm exploring Scout Pro. Can you explain how Scout Pro would help when a team member leaves my company — which contacts, certifications, and reports you'd check in our Encamp data, how you'd identify what needs updating, and what kinds of data you can see and changes you can make that Regulatory Advisor cannot?",
        requiresDataAccess: true,
        requiresSandbox: false,
      },
      {
        label: "Tell me about how my RCRA reporting requirements have changed since last season.",
        prompt:
          "I'm exploring Scout Pro. Can you explain how Scout Pro would compare my RCRA reporting requirements and obligations since last season — what you'd pull from our facilities and waste data, how you'd surface year-over-year changes, and what value that guided review provides compared to general RCRA guidance on Regulatory Advisor alone?",
        requiresDataAccess: true,
        requiresSandbox: false,
      },
    ],
  },
];

/** How many starter prompts Pro customers see per category (sampled at random). */
export const PRO_SUGGESTIONS_PER_CATEGORY = 4;

export type ScoutSuggestionAudience = "regulatory-advisor" | "pro";

export function isRegulatoryAdvisorSafeSuggestion(item: ScoutSuggestion): boolean {
  return !item.requiresDataAccess && !item.requiresSandbox;
}

/**
 * Sample up to `count` items uniformly without replacement.
 * `random` is injectable so unit tests can make the shuffle deterministic.
 */
export function sampleScoutSuggestions<T>(
  items: readonly T[],
  count: number,
  random: () => number = Math.random
): T[] {
  if (count <= 0) {
    return [];
  }
  if (items.length <= count) {
    return [...items];
  }

  const pool = [...items];
  for (let i = pool.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    const current = pool[i];
    const swap = pool[j];
    if (current === undefined || swap === undefined) {
      continue;
    }
    pool[i] = swap;
    pool[j] = current;
  }
  return pool.slice(0, count);
}

/**
 * Categories shown on the new-chat screen for the current Scout access package.
 *
 * - Regulatory Advisor: knowledge-only prompts in the first four categories, plus
 *   the full My Program Pro upsell category.
 * - Pro: My Program hidden; each remaining category shows a random sample of
 *   {@link PRO_SUGGESTIONS_PER_CATEGORY} prompts.
 */
export function getVisibleScoutSuggestionCategories({
  audience,
  random = Math.random,
}: {
  audience: ScoutSuggestionAudience;
  random?: () => number;
}): ScoutSuggestionCategory[] {
  if (audience === "regulatory-advisor") {
    return SCOUT_SUGGESTION_CATEGORIES.map((category) => {
      if (category.key === MY_PROGRAM_CATEGORY_KEY) {
        return category;
      }
      return {
        ...category,
        items: category.items.filter(isRegulatoryAdvisorSafeSuggestion),
      };
    });
  }

  return SCOUT_SUGGESTION_CATEGORIES.filter(
    (category) => category.key !== MY_PROGRAM_CATEGORY_KEY
  ).map((category) => ({
    ...category,
    items: sampleScoutSuggestions(category.items, PRO_SUGGESTIONS_PER_CATEGORY, random),
  }));
}
